/* Design-savvy node-kind icons — Tabler Icons (16k★ MIT, ~4500 icons,
 * stroke-only Apple-style aesthetic). Free + open-source, the same
 * library used by Vercel, Linear-adjacent design systems, Spotify
 * internal tools.
 *
 * Two-level dispatch: NodeKind first (Project/Room/SKU/…), then a
 * label-aware refinement so a "Sofa 3-seater" renders IconArmchair2, a
 * "Pendant chandelier" renders IconLamp2, a "Master Bedroom" renders
 * IconBed, etc. This is what makes the graph read as a *designer's*
 * vocabulary instead of a generic UI glyph dump.
 */

import {
  // Generic kinds
  IconBuilding, IconUser, IconHeart, IconSparkles,
  IconHome2, IconMapPin, IconShoppingBag, IconColorSwatch,
  IconBuildingStore, IconCoin, IconFlag, IconFileCheck,
  IconGavel, IconBell, IconBookmark, IconBrain, IconCpu,
  IconDatabase, IconPlug, IconGitBranch, IconBolt, IconRefresh,
  IconPalette, IconArrowDown, IconClock, IconBox, IconLayout2,
  // Interior-design domain refinements
  IconArmchair2, IconBed, IconLamp2, IconBath,
  IconChefHat, IconArchive, IconCooker, IconBorderAll,
  IconBulb, IconTable, IconDeviceDesktop,
} from "@tabler/icons-react";
import type { Icon as TablerIcon } from "@tabler/icons-react";

type IconCmp = TablerIcon;

/* Per-NodeKind default icon — used when no label hint is available. */
const KIND_ICON: Record<string, IconCmp> = {
  Project:    IconBuilding,     project:    IconBuilding,
  Client:     IconUser,         client:     IconUser,
  Intent:     IconHeart,        intent:     IconHeart,
  Style:      IconSparkles,     style:      IconSparkles,
  Room:       IconHome2,        room:       IconHome2,
  Zone:       IconMapPin,       zone:       IconMapPin,
  SKU:        IconShoppingBag,  sku:        IconShoppingBag,
  Material:   IconColorSwatch,  material:   IconColorSwatch,
  Vendor:     IconBuildingStore,vendor:     IconBuildingStore,
  BudgetLine: IconCoin,         budgetline: IconCoin,
  Milestone:  IconFlag,         milestone:  IconFlag,
  Approval:   IconFileCheck,    approval:   IconFileCheck,
  Rule:       IconGavel,        rule:       IconGavel,
  Alert:      IconBell,         alert:      IconBell,
  MemoryCard: IconBookmark,     memorycard: IconBookmark,
  FloorPlan:  IconLayout2,    floorplan:  IconLayout2,
  // Agentic mesh slots
  external:   IconPlug,
  protocol:   IconGitBranch,
  supervisor: IconCpu,
  specialist: IconBrain,
  store:      IconDatabase,
  // Ontology super-kinds
  place:      IconMapPin,
  thing:      IconBox,
  actor:      IconUser,
  memory:     IconBookmark,
  money:      IconCoin,
  time:       IconClock,
  // Flow-step kinds
  trigger:    IconBolt,
  automation: IconRefresh,
  agent:      IconBrain,
  moodboard:  IconPalette,
  catalog:    IconShoppingBag,
  out:        IconArrowDown,
};

/** Typed subtype → icon. Single dispatch table by (NodeKind, subtype).
 *  Subtypes are typed enums on the node (`attrs.category` for SKU,
 *  `attrs.roomKind` for Room, `attrs.zoneKind` for Zone). Source of
 *  truth is the graph data, never the display label.
 *
 *  For external/unstructured ingest (e.g. raw Kaggle catalogues
 *  without a typed category), a nemotron-via-APISIX classifier runs
 *  at INGEST time — see `agent/foyr_agent/agents/sku_classifier.py`.
 *  Its output is cached to a generated TS table, so the browser does
 *  zero runtime LLM work. Build-time classifier pattern. */
const SUBTYPE_ICON: Record<string, Record<string, IconCmp>> = {
  // SKU subtypes — matches `SKU_POOL.key` in data/sampleGraph.ts.
  sku: {
    sofa:        IconArmchair2,
    pendant:     IconLamp2,
    bulb:        IconBulb,
    bunk:        IconBed,
    bed:         IconBed,
    wardrobe:    IconArchive,
    cabinet:     IconArchive,
    storage:     IconArchive,
    island:      IconCooker,
    kitchen:     IconCooker,
    modular:     IconCooker,
    cooker:      IconCooker,
    quartz:      IconBorderAll,
    countertop:  IconBorderAll,
    granite:     IconBorderAll,
    diningtbl:   IconTable,
    table:       IconTable,
    studyunit:   IconDeviceDesktop,
    desk:        IconDeviceDesktop,
    workstation: IconDeviceDesktop,
    homebar:     IconArchive,
    tvunit:      IconDeviceDesktop,
    bath:        IconBath,
    rug:         IconArmchair2,
  },
  // Room subtypes — matches `roomKind` in data/sampleGraph.ts `pickRooms`.
  room: {
    living:      IconArmchair2,
    kitchen:     IconChefHat,
    bedroom:     IconBed,
    kidsbedroom: IconBed,
    dining:      IconTable,
    bath:        IconBath,
    foyer:       IconHome2,
  },
  // Zone subtypes — by activity.
  zone: {
    cook:        IconChefHat,
    sleep:       IconBed,
    read:        IconBorderAll,
    study:       IconBorderAll,
    relax:       IconArmchair2,
    lounge:      IconArmchair2,
  },
};

/** Domain-specific Tabler icon for a typed graph node.
 *
 *   1. Typed `subtype` attr (data-driven — SKU.category / Room.roomKind /
 *      Zone.zoneKind, populated at ingest time by either the authored
 *      pool or the nemotron classifier agent).
 *   2. NodeKind default.
 *
 *   No regex on display labels. No string heuristics. The graph is
 *   the contract. */
export function iconForGraphNode(kind: string, subtype = ""): IconCmp {
  const k = kind.toLowerCase();
  const s = subtype.toLowerCase();
  const sub = SUBTYPE_ICON[k];
  if (sub && s && sub[s]) return sub[s]!;
  return KIND_ICON[kind] ?? IconBox;
}

interface NodeKindIconProps {
  kind: string;
  /** Typed subtype on the node — SKU.category / Room.roomKind /
   *  Zone.zoneKind. Single source of truth for icon dispatch. */
  subtype?: string;
  /** @deprecated alias for `subtype`; kept so older callers compile. */
  category?: string;
  /** Retained for API compatibility — never used for dispatch. */
  label?: string;
  /** Retained for API compatibility with prior Phosphor call sites. */
  weight?: string;
  size?: number;
  color?: string;
  className?: string;
  /** Tabler stroke width in pixels — 1.6 reads as Apple-thin. */
  stroke?: number;
}

export function NodeKindIcon({
  kind, subtype, category, size = 18, color, className, stroke = 1.6,
}: NodeKindIconProps): JSX.Element {
  const Icon = iconForGraphNode(kind, subtype ?? category ?? "");
  return (
    <Icon
      size={size}
      stroke={stroke}
      {...(color ? { color } : {})}
      {...(className ? { className } : {})}
    />
  );
}

export { KIND_ICON };
